import { Button } from '@mm/components';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel, {
  FilterItem,
  FilterParams,
  SystemFilterItem,
  TagFilterItem,
  TagsFilters,
} from '@mm/state/model/AudiencesModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { getUniqId } from '@mm/utils';
import { Menu, MenuItem } from '@szhsin/react-menu';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon } from '@mm/icons/ComponentIcons';
import { OnClickElsewhere } from '../../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { colors } from '../../../styles/colors';
import styles from './FilterBar.module.css';
import { FilterBarItem } from './FilterBarItem';

interface Props {
  activeAudience?: AudiencesModel;
  onFiltersUpdated: (
    filterParams: FilterParams | undefined,
    tagsFilters: TagsFilters | undefined,
  ) => void;
  totalFound?: number;
}
interface FilterEditAttributes {
  dirty: boolean;
  uniqId: string;
  deleted?: boolean;
  new?: boolean;
}

type PendingFilter =
  | ({ owner: 'filterParams' } & (FilterItem | SystemFilterItem) &
      FilterEditAttributes)
  | ({ owner: 'tagsFilters' } & TagFilterItem & FilterEditAttributes);

export const FilterBar = (props: Props) => {
  const { activeAudience, onFiltersUpdated, totalFound } = props;
  const { id: activeAudienceId } = activeAudience || {};
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const [pendingFilters, setPendingFilters] = useState<PendingFilter[]>([]);
  const [filterUnderEdit, setFilterUnderEdit] = useState<{
    isNew?: boolean;
    uniqId: string;
  }>();
  const onCancelEdit = () => {
    if (filterUnderEdit?.isNew) {
      setPendingFilters(filters =>
        filters.filter(({ uniqId }) => uniqId !== filterUnderEdit.uniqId),
      );
    }
    setFilterUnderEdit(undefined);
  };
  const onAddFilter = (type: 'attributes' | 'platform' | 'tags') => {
    onCancelEdit();
    const uniqId = getUniqId('filter');

    if (type === 'tags') {
      setPendingFilters(pendingFilters => [
        ...pendingFilters,
        {
          owner: 'tagsFilters',
          filterType: 'tags_filters',
          values: [],
          operator: 'include',
          dirty: true,
          new: true,
          uniqId,
        },
      ]);
    } else {
      setPendingFilters(pendingFilters => [
        ...pendingFilters,
        {
          owner: 'filterParams',
          filterType: type === 'attributes' ? 'variable_values' : 'platforms',
          value: [],
          dirty: true,
          new: true,
          uniqId,
        },
      ]);
    }
    setFilterUnderEdit({ uniqId, isNew: true });
  };
  const onUpdateFilter = (uniqId: string) => updatedFilter => {
    setPendingFilters(filters => {
      return filters
        .map(oldFilter => {
          if (oldFilter.uniqId !== uniqId) {
            return oldFilter;
          }
          // undefined means delete this filter
          if (updatedFilter) {
            return { ...updatedFilter, dirty: true };
          }

          return oldFilter.new ? null : { deleted: true, dirty: true };
        })
        .filter(Boolean);
    });
    setFilterUnderEdit(undefined);
  };
  useEffect(() => {
    if (!filterUnderEdit) {
      const somethingChanged = pendingFilters.some(fp => fp.dirty);
      if (!somethingChanged) {
        onFiltersUpdated(undefined, undefined);
        return;
      }
      // Strip off local attributes that shouldn't be sent to the BE
      const cleanupFilter = ({
        dirty: _dirty,
        owner: _owner,
        new: _new = false,
        ...filter
      }) => ({
        ...filter,
      });

      const filterParams = pendingFilters
        .filter(f => !f.deleted && f.owner === 'filterParams')
        .map(cleanupFilter);
      const tagsFilters = pendingFilters
        .filter(f => !f.deleted && f.owner === 'tagsFilters')
        .map(cleanupFilter);
      onFiltersUpdated(
        {
          ...(filterParams.length ? { logic: '1' } : {}),
          ...filterParams.reduce((acc, filter, i) => {
            acc[i + 1] = filter;
            return acc;
          }, {}),
        },
        tagsFilters.reduce((acc, filter, i) => {
          acc[i + 1] = filter;
          return acc;
        }, {}),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUnderEdit, pendingFilters]);
  useEffect(() => {
    (async () => {
      setFilterUnderEdit(undefined);
      if (!activeAudienceId) {
        setPendingFilters([]);
        return;
      }
      const audience = await audiencesCRUDActions.retrieveAudienceById(
        activeChatbotId,
        activeAudienceId,
      );
      setPendingFilters([
        ...Object.entries(audience?.filterParams || {})
          .filter(([k, _v]) => k !== 'logic')
          .map(([_k, v]) => ({
            ...v,
            owner: 'filterParams',
            dirty: false,
            uniqId: getUniqId('filterParams'),
          })),

        ...Object.values(audience?.tagsFilters || {}).map(f => ({
          ...f,
          owner: 'tagsFilters',
          dirty: false,
          uniqId: getUniqId('tagsFilters'),
        })),
      ]);
    })();
  }, [activeAudienceId, activeChatbotId, audiencesCRUDActions]);

  return (
    <OnClickElsewhere handleClick={onCancelEdit} className={styles.container}>
      <div className={styles.filterBar}>
        {pendingFilters
          .filter(f => !f.deleted)
          .map(filter => (
            <FilterBarItem
              key={filter.uniqId}
              filter={filter}
              onUpdate={onUpdateFilter(filter.uniqId)}
              onStartEdit={() => {
                onCancelEdit();
                setFilterUnderEdit({ uniqId: filter.uniqId });
              }}
              onCancelEdit={onCancelEdit}
              editing={filterUnderEdit?.uniqId === filter.uniqId}
            />
          ))}

        <div>
          <Menu
            menuButton={
              <Button
                color="link"
                leftIcon={<PlusIcon color={colors.accentBlue} size={16} />}
              >
                Add filter
              </Button>
            }
          >
            <MenuItem
              onClick={() => onAddFilter('platform')}
              disabled={pendingFilters.some(f => f.filterType === 'platforms')}
            >
              Platform
            </MenuItem>
            <MenuItem onClick={() => onAddFilter('tags')}>Tags</MenuItem>
            <MenuItem onClick={() => onAddFilter('attributes')}>
              Attributes
            </MenuItem>
          </Menu>
        </div>
      </div>
      {totalFound !== undefined && (
        <div className={styles.total}>
          Found <span className={styles.totalCount}>{totalFound}</span> items
        </div>
      )}
    </OnClickElsewhere>
  );
};
